import React from 'react';
import get from 'lodash/get';
import Spacer from 'common/components/Spacer';
import { useEditor } from '@splashthat/splash-cms-engine';
import { SidebarPanel, SPACING } from 'common/components/TouchpointEditor/components/TouchpointSidebar';
import { useTouchpointData } from 'common/components/TouchpointEditor/contexts/TouchpointDataContext';
import { containerPaddingL } from 'common/styles/variables';
import {
    NodeButton,
    NodeContainer,
} from 'common/components/TouchpointEditor/components/TouchpointSidebar/ElementsListMenu';
import ShowHideButton from 'common/components/TouchpointEditor/components/TouchpointSidebar/ShowHideButton';
import LayoutOptionsMenu from 'common/components/LayoutOptionsMenu';
import TridotOption from 'common/components/TridotOption';

import { CONTENT_TYPE_EMBED_ZOOM } from 'common/constants/virtualEvent';

import {
    NODE_ID_PAGE_CONTAINER,
    NODE_ID_HEADER,
    NODE_ID_HEADLINE,
    NODE_ID_LOGO,
    NODE_ID_VIRTUAL_CONTENT,
    NODE_ID_EMBED,
    NODE_ID_ALT_EMBED,
    NODE_ID_LINK_OUT,
    NODE_ID_ZOOM_LINK_OUT,
    NODE_ID_FOOTER_CONTAINER,
    NODE_ID_CONTACT_ORGANIZER_BUTTON,
    NODE_ID_VISIT_EVENT_PAGE_BUTTON,
    NODE_ID_CUSTOM_BUTTON,
    NODE_DISPLAY_NAME_PAGE_CONTAINER,
    NODE_DISPLAY_NAME_HEADER,
    NODE_DISPLAY_NAME_HEADLINE,
    NODE_DISPLAY_NAME_LOGO,
    NODE_DISPLAY_NAME_VIRTUAL_CONTENT,
    NODE_DISPLAY_NAME_LINK_OUT,
    NODE_DISPLAY_NAME_ZOOM_LINK_OUT,
    NODE_DISPLAY_NAME_FOOTER_CONTAINER,
    NODE_DISPLAY_NAME_CONTACT_ORGANIZER_BUTTON,
    NODE_DISPLAY_NAME_VISIT_EVENT_PAGE_BUTTON,
    NODE_DISPLAY_NAME_CUSTOM_BUTTON,
    HEADER_LAYOUT_OPTIONS_WITH_ICONS,
    VIRTUAL_CONTENT_LAYOUT_OPTIONS_WITH_ICONS,
    VIRTUAL_CONTENT_TYPE_EMBED,
    VIRTUAL_CONTENT_TYPE_LINK_OUT,
    NODE_ID_CUSTOM_BUTTON_TWO,
    NODE_DISPLAY_NAME_CUSTOM_BUTTON_TWO,
} from '../../../constants';
import RemoveButton from '../RemoveButton';

import {
    HeaderWithLayoutContainer,
    VirtualContentWithLayoutContainer,
    AddEmbedButton,
} from './ElementsListMenu.styles';

const BASE_CLASS_NAME = 'ElementsListMenu';
const ADD_EMBED_BUTTON_LABEL = '+ Add Embed';
const HEADER_LAYOUT_OPTIONS_ARIA_LABEL = `Open ${NODE_DISPLAY_NAME_HEADER} Layout Options Menu`;
const VIRTUAL_CONTENT_LAYOUT_OPTIONS_ARIA_LABEL = `Open ${NODE_DISPLAY_NAME_VIRTUAL_CONTENT} Layout Options Menu`;
const VIRTUAL_CONTENT_EMBED_OPTIONS_ARIA_LABEL = `Open ${NODE_DISPLAY_NAME_VIRTUAL_CONTENT} Embed Options Menu`;

export default function ElementsListMenu() {
    const { embeds } = useTouchpointData();
    const editor = useEditor((state) => ({
        nodes: {
            [NODE_ID_CUSTOM_BUTTON]: state.nodes[NODE_ID_CUSTOM_BUTTON],
            [NODE_ID_CUSTOM_BUTTON_TWO]: state.nodes[NODE_ID_CUSTOM_BUTTON_TWO],
            [NODE_ID_VIRTUAL_CONTENT]: state.nodes[NODE_ID_VIRTUAL_CONTENT],
            [NODE_ID_ALT_EMBED]: state.nodes[NODE_ID_ALT_EMBED],
            [NODE_ID_EMBED]: state.nodes[NODE_ID_EMBED],
            [NODE_ID_ZOOM_LINK_OUT]: state.nodes[NODE_ID_ZOOM_LINK_OUT],
        },
    }));

    const isVirtualContentTypeLinkOut =
        get(editor.nodes, [NODE_ID_VIRTUAL_CONTENT, 'meta', 'virtualContentType']) === VIRTUAL_CONTENT_TYPE_LINK_OUT;

    const showShowAltEmbed = embeds.data.shouldRenderAltEmbed && !isVirtualContentTypeLinkOut;
    const shouldShowLayoutOptions =
        embeds.data.hasAltEmbed && embeds.data.shouldRenderAltEmbed && !isVirtualContentTypeLinkOut;
    const shouldShowZoomLinkOut =
        !isVirtualContentTypeLinkOut && embeds?.data?.embedConfig?.type === CONTENT_TYPE_EMBED_ZOOM;

    const selectNode = (e, id) => {
        e.stopPropagation();
        editor.actions.selectNode(id);
    };

    const toggleNodeVisibility = (e, id) => {
        e.stopPropagation();
        editor.actions.setNodeProps(id, (node) => {
            // eslint-disable-next-line no-param-reassign
            node.meta.visible = !node.meta.visible;

            return node;
        });
    };

    const onChangeLayout = (layout, nodeId) =>
        editor.actions.setNodeProps(nodeId, (editedNode) => {
            // eslint-disable-next-line no-param-reassign
            editedNode.meta.layout = layout;
            return editedNode;
        });

    const updateVirtualContentType = (type) => {
        editor.actions.setNodeProps(NODE_ID_VIRTUAL_CONTENT, (node) => {
            // eslint-disable-next-line no-param-reassign
            node.meta.virtualContentType = type;

            return node;
        });
    };

    const virtualContentTypeOptions = [
        {
            label: 'Embed',
            onClick: (e) => {
                e.stopPropagation();
                updateVirtualContentType(VIRTUAL_CONTENT_TYPE_EMBED);
            },
        },
        {
            label: 'Link Out',
            onClick: (e) => {
                e.stopPropagation();
                updateVirtualContentType(VIRTUAL_CONTENT_TYPE_LINK_OUT);
            },
        },
    ];

    return (
        <SidebarPanel variation="padded">
            <NodeButton
                variation="secondary"
                label={NODE_DISPLAY_NAME_PAGE_CONTAINER}
                qaId={NODE_ID_PAGE_CONTAINER}
                onClick={(e) => selectNode(e, NODE_ID_PAGE_CONTAINER)}
                onMouseEnter={() => editor.actions.hoverNode(NODE_ID_PAGE_CONTAINER)}
                onMouseLeave={() => editor.actions.unhoverAllNodes()}
                bold
            />
            <Spacer $spacing={containerPaddingL} />
            <NodeContainer>
                <HeaderWithLayoutContainer>
                    <NodeButton
                        paddingLeft="0"
                        variation="navigation"
                        label={NODE_DISPLAY_NAME_HEADER}
                        qaId={NODE_ID_HEADER}
                        onClick={(e) => selectNode(e, NODE_ID_HEADER)}
                        onMouseEnter={() => editor.actions.hoverNode(NODE_ID_HEADER)}
                        onMouseLeave={() => editor.actions.unhoverAllNodes()}
                        bold
                    />
                    <LayoutOptionsMenu
                        options={HEADER_LAYOUT_OPTIONS_WITH_ICONS}
                        onChange={(value) => onChangeLayout(value, NODE_ID_HEADER)}
                        qaId={BASE_CLASS_NAME}
                        ariaLabel={HEADER_LAYOUT_OPTIONS_ARIA_LABEL}
                    />
                </HeaderWithLayoutContainer>
                <NodeButton
                    label={NODE_DISPLAY_NAME_HEADLINE}
                    qaId={NODE_ID_HEADLINE}
                    onClick={(e) => selectNode(e, NODE_ID_HEADLINE)}
                    onMouseEnter={() => editor.actions.hoverNode(NODE_ID_HEADLINE)}
                    onMouseLeave={() => editor.actions.unhoverAllNodes()}
                    variation="secondary"
                />

                <Spacer $spacing={SPACING} />
                <NodeButton
                    label={NODE_DISPLAY_NAME_LOGO}
                    qaId={NODE_ID_LOGO}
                    onClick={(e) => selectNode(e, NODE_ID_LOGO)}
                    onMouseEnter={() => editor.actions.hoverNode(NODE_ID_LOGO)}
                    onMouseLeave={() => editor.actions.unhoverAllNodes()}
                    variation="secondary"
                />
            </NodeContainer>
            <Spacer $spacing={containerPaddingL} />
            <NodeContainer>
                <VirtualContentWithLayoutContainer>
                    <NodeButton
                        label={NODE_DISPLAY_NAME_VIRTUAL_CONTENT}
                        qaId={NODE_ID_VIRTUAL_CONTENT}
                        onClick={(e) => selectNode(e, NODE_ID_VIRTUAL_CONTENT)}
                        onMouseEnter={() => editor.actions.hoverNode(NODE_ID_VIRTUAL_CONTENT)}
                        onMouseLeave={() => editor.actions.unhoverAllNodes()}
                        paddingLeft="0"
                        variation="navigation"
                        bold
                    />
                    {shouldShowLayoutOptions && (
                        <LayoutOptionsMenu
                            options={VIRTUAL_CONTENT_LAYOUT_OPTIONS_WITH_ICONS}
                            onChange={(value) => onChangeLayout(value, NODE_ID_VIRTUAL_CONTENT)}
                            qaId={BASE_CLASS_NAME}
                            ariaLabel={VIRTUAL_CONTENT_LAYOUT_OPTIONS_ARIA_LABEL}
                        />
                    )}
                    <TridotOption
                        options={virtualContentTypeOptions}
                        qaId={NODE_ID_VIRTUAL_CONTENT}
                        ariaLabel={VIRTUAL_CONTENT_EMBED_OPTIONS_ARIA_LABEL}
                    />
                </VirtualContentWithLayoutContainer>

                <Spacer $spacing={SPACING} />
                {isVirtualContentTypeLinkOut ? (
                    <NodeButton
                        label={NODE_DISPLAY_NAME_LINK_OUT}
                        qaId={NODE_ID_LINK_OUT}
                        onClick={(e) => selectNode(e, NODE_ID_LINK_OUT)}
                        onMouseEnter={() => editor.actions.hoverNode(NODE_ID_LINK_OUT)}
                        onMouseLeave={() => editor.actions.unhoverAllNodes()}
                        variation="secondary"
                    />
                ) : (
                    <NodeButton
                        label={editor.nodes[NODE_ID_EMBED]?.displayName}
                        qaId={NODE_ID_EMBED}
                        onClick={(e) => selectNode(e, NODE_ID_EMBED)}
                        onMouseEnter={() => editor.actions.hoverNode(NODE_ID_EMBED)}
                        onMouseLeave={() => editor.actions.unhoverAllNodes()}
                        variation="secondary"
                    />
                )}

                {shouldShowZoomLinkOut && (
                    <>
                        <Spacer $spacing={SPACING} />
                        <NodeButton
                            label={NODE_DISPLAY_NAME_ZOOM_LINK_OUT}
                            qaId={NODE_ID_ZOOM_LINK_OUT}
                            onClick={(e) => selectNode(e, NODE_ID_ZOOM_LINK_OUT)}
                            onMouseEnter={() => editor.actions.hoverNode(NODE_ID_ZOOM_LINK_OUT)}
                            onMouseLeave={() => editor.actions.unhoverAllNodes()}
                            variation="secondary"
                            iconAfter={
                                <ShowHideButton
                                    handleToggle={(e) => toggleNodeVisibility(e, NODE_ID_ZOOM_LINK_OUT)}
                                    visible={get(editor.nodes, [NODE_ID_ZOOM_LINK_OUT, 'meta', 'visible'])}
                                />
                            }
                        />
                    </>
                )}

                <Spacer $spacing={SPACING} />

                {showShowAltEmbed &&
                    (!embeds.data.hasAltEmbed ? (
                        <AddEmbedButton
                            label={ADD_EMBED_BUTTON_LABEL}
                            variation="secondary"
                            onClick={() => embeds.actions.updateHasAltEmbed(true)}
                        />
                    ) : (
                        <NodeButton
                            label={editor.nodes[NODE_ID_ALT_EMBED]?.displayName}
                            qaId={NODE_ID_ALT_EMBED}
                            onClick={(e) => selectNode(e, NODE_ID_ALT_EMBED)}
                            onMouseEnter={() => editor.actions.hoverNode(NODE_ID_ALT_EMBED)}
                            onMouseLeave={() => editor.actions.unhoverAllNodes()}
                            variation="secondary"
                            iconAfter={
                                <RemoveButton
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        embeds.actions.updateHasAltEmbed(false);
                                    }}
                                    ariaLabel="Remove secondary embed"
                                />
                            }
                        />
                    ))}
            </NodeContainer>
            <Spacer $spacing={containerPaddingL} />
            <NodeContainer>
                <NodeButton
                    paddingLeft="0"
                    variation="navigation"
                    label={NODE_DISPLAY_NAME_FOOTER_CONTAINER}
                    qaId={NODE_ID_FOOTER_CONTAINER}
                    onClick={(e) => selectNode(e, NODE_ID_FOOTER_CONTAINER)}
                    onMouseEnter={() => editor.actions.hoverNode(NODE_ID_FOOTER_CONTAINER)}
                    onMouseLeave={() => editor.actions.unhoverAllNodes()}
                    bold
                />
                <NodeButton
                    variation="secondary"
                    label={NODE_DISPLAY_NAME_CONTACT_ORGANIZER_BUTTON}
                    qaId={NODE_ID_CONTACT_ORGANIZER_BUTTON}
                    onClick={(e) => selectNode(e, NODE_ID_CONTACT_ORGANIZER_BUTTON)}
                    onMouseEnter={() => editor.actions.hoverNode(NODE_ID_CONTACT_ORGANIZER_BUTTON)}
                    onMouseLeave={() => editor.actions.unhoverAllNodes()}
                />
                <Spacer $spacing={SPACING} />
                <NodeButton
                    variation="secondary"
                    label={NODE_DISPLAY_NAME_VISIT_EVENT_PAGE_BUTTON}
                    qaId={NODE_ID_VISIT_EVENT_PAGE_BUTTON}
                    onClick={(e) => selectNode(e, NODE_ID_VISIT_EVENT_PAGE_BUTTON)}
                    onMouseEnter={() => editor.actions.hoverNode(NODE_ID_VISIT_EVENT_PAGE_BUTTON)}
                    onMouseLeave={() => editor.actions.unhoverAllNodes()}
                />
                <Spacer $spacing={SPACING} />
                <NodeButton
                    variation="secondary"
                    label={NODE_DISPLAY_NAME_CUSTOM_BUTTON}
                    qaId={NODE_ID_CUSTOM_BUTTON}
                    onClick={(e) => selectNode(e, NODE_ID_CUSTOM_BUTTON)}
                    onMouseEnter={() => editor.actions.hoverNode(NODE_ID_CUSTOM_BUTTON)}
                    onMouseLeave={() => editor.actions.unhoverAllNodes()}
                    iconAfter={
                        <ShowHideButton
                            handleToggle={(e) => toggleNodeVisibility(e, NODE_ID_CUSTOM_BUTTON)}
                            visible={get(editor.nodes, [NODE_ID_CUSTOM_BUTTON, 'meta', 'visible'])}
                        />
                    }
                />
                <Spacer $spacing={SPACING} />
                <NodeButton
                    variation="secondary"
                    label={NODE_DISPLAY_NAME_CUSTOM_BUTTON_TWO}
                    qaId={NODE_ID_CUSTOM_BUTTON_TWO}
                    onClick={(e) => selectNode(e, NODE_ID_CUSTOM_BUTTON_TWO)}
                    onMouseEnter={() => editor.actions.hoverNode(NODE_ID_CUSTOM_BUTTON_TWO)}
                    onMouseLeave={() => editor.actions.unhoverAllNodes()}
                    iconAfter={
                        <ShowHideButton
                            handleToggle={(e) => toggleNodeVisibility(e, NODE_ID_CUSTOM_BUTTON_TWO)}
                            visible={get(editor.nodes, [NODE_ID_CUSTOM_BUTTON_TWO, 'meta', 'visible'])}
                        />
                    }
                />
            </NodeContainer>
        </SidebarPanel>
    );
}
